import React from 'react';
import { formatDate } from '../../../utils/calendarHelpers';

/**
 * FloatingOverlay - Overlay for floating calendar availability blocks
 * Renders draggable availability blocks on top of the week grid
 */
const FloatingOverlay = ({
  weekDays,
  availability,
  isAdmin,
  dragState,
  setDragState,
  setSelectedSlot,
  setShowAppointmentModal,
  setContextMenu,
  setSelectedAvailability,
  token
}) => {
  return (
    <div className="absolute top-[73px] left-0 right-0 bottom-0 pointer-events-none" style={{ zIndex: 5 }}>
      <div className="grid grid-cols-8 h-full">
        <div className="border-r"></div>
        {weekDays.map((day, dayIdx) => {
          const dateStr = formatDate(day);
          // Get all availability for this day
          const dayAvailability = availability.filter(avail => 
            avail.date === dateStr && 
            avail.is_available &&
            avail.calendar_id &&
            !avail.server_id
          );
          
          return (
            <div key={dayIdx} className="relative border-r last:border-r-0">
              {dayAvailability.map((avail, availIdx) => {
                // Calculate position and height
                const [startHour, startMin] = avail.start_time.split(':').map(Number);
                const [endHour, endMin] = avail.end_time.split(':').map(Number);
                
                const startDecimal = startHour + (startMin / 60);
                const endDecimal = endHour + (endMin / 60);
                const duration = endDecimal - startDecimal;
                
                // Each hour row is 80px (min-h-[80px])
                const pixelsPerHour = 80;
                const topPosition = startDecimal * pixelsPerHour;
                const heightValue = duration * pixelsPerHour;
                
                // Check if this availability block is in the past
                const availDateTime = new Date(day);
                availDateTime.setHours(endHour, endMin, 0, 0);
                const isAvailPast = availDateTime < new Date();
                
                // Determine styling based on hold status and past status
                const isOnHold = avail.is_on_hold === true;
                
                // Past slots get grey styling
                let bgColor, borderColor, textColor, subtextColor, dragHandleColor, statusText;
                if (isAvailPast) {
                  bgColor = 'bg-gray-200';
                  borderColor = 'border-gray-400';
                  textColor = 'text-gray-500';
                  subtextColor = 'text-gray-500';
                  dragHandleColor = 'bg-gray-500';
                  statusText = 'Past';
                } else if (isOnHold) {
                  bgColor = 'bg-orange-100';
                  borderColor = 'border-orange-400';
                  textColor = 'text-orange-700';
                  subtextColor = 'text-orange-600';
                  dragHandleColor = 'bg-orange-600';
                  statusText = 'On Hold';
                } else {
                  bgColor = 'bg-green-100';
                  borderColor = 'border-green-400';
                  textColor = 'text-green-700';
                  subtextColor = 'text-green-600';
                  dragHandleColor = 'bg-green-600';
                  statusText = 'Available';
                }
                
                return (
                  <div
                    key={availIdx}
                    className={`absolute left-0 right-0 ${bgColor} border-2 ${borderColor} rounded-md flex flex-col items-center justify-center pointer-events-auto group`}
                    style={{
                      top: `${topPosition}px`,
                      height: `${heightValue}px`
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isAdmin() && !dragState) {
                        // Calculate the clicked time based on Y position within the block
                        const rect = e.currentTarget.getBoundingClientRect();
                        const relativeY = (e.clientY - rect.top) / rect.height;
                        const clickedDecimal = startDecimal + (duration * relativeY);
                        const clickedHour = Math.floor(clickedDecimal);
                        const clickedMinutes = Math.round((clickedDecimal - clickedHour) * 60);
                        const roundedMinutes = Math.floor(clickedMinutes / 15) * 15;
                        
                        setSelectedSlot({
                          date: dateStr,
                          hour: clickedHour,
                          time: `${clickedHour.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`
                        });
                        setShowAppointmentModal(true);
                      }
                    }}
                    onContextMenu={(e) => {
                      if (isAdmin()) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        // Check if this is a past slot
                        const slotDateTime = new Date(day);
                        slotDateTime.setHours(startHour, startMin, 0, 0);
                        const isPastSlot = slotDateTime < new Date();
                        
                        setContextMenu({
                          x: e.clientX,
                          y: e.clientY,
                          hasAvailability: true,
                          isBookable: true,
                          isPastSlot: isPastSlot
                        });
                        setSelectedAvailability(avail);
                      }
                    }}
                  >
                    {/* Top drag handle */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 ${dragHandleColor} rounded-t-md transition-opacity`}
                      onMouseDown={(e) => {
                        if (isAdmin()) {
                          e.stopPropagation();
                          setDragState({
                            availId: avail.id,
                            edge: 'top',
                            startY: e.clientY,
                            originalStartTime: avail.start_time,
                            originalEndTime: avail.end_time,
                            date: dateStr
                          });
                        }
                      }}
                    />
                    
                    {/* Content */}
                    <div className="pointer-events-none flex flex-col items-center justify-center">
                      <div className={`text-xs font-semibold ${textColor}`}>{statusText}</div>
                      <div className={`text-xs ${subtextColor} mt-1`}>
                        {avail.start_time} - {avail.end_time}
                      </div>
                    </div>
                    
                    {/* Bottom drag handle */}
                    <div
                      className={`absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 ${dragHandleColor} rounded-b-md transition-opacity`}
                      onMouseDown={(e) => {
                        if (isAdmin()) {
                          e.stopPropagation();
                          setDragState({
                            availId: avail.id,
                            edge: 'bottom',
                            startY: e.clientY,
                            originalStartTime: avail.start_time,
                            originalEndTime: avail.end_time,
                            date: dateStr
                          });
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FloatingOverlay;
